<script lang="ts">
    /*
value = quelque chose de compréhensible pour `toLuxon()`,
    ou `'now'` pour 'maintenant'
    ou ``'live'` pour 'now', mais updaté aux secondes
    Si `null` ou `''`, retourne un en-dash (–)
format
- un preset luxon en string
  https://moment.github.io/luxon/index.html#/formatting?id=presets
  qui sera passé à toLocaleString
- un string de tokens, passé à toLocaleString()
  https://moment.github.io/luxon/index.html#/formatting?id=table-of-tokens
- valeurs spéciales `'h-table'` et `'h-table-s'`
  Produit un format "langue française / ISO" avec HTML
  pour que ça sorte "aligné" dans une table HTML.
  Ajoute aussi features de fonts via CSS pour que ça aligne.
- `'table'` et `'table-s'` format "tableau" pour que ça aligne.
  Probablement useless avec le h-table-*.
*/
    import { onMount } from 'svelte'

    import { DateTime, alignToServer, luxonLocale, toLuxon } from 'hydria/utils/time'

    interface Props {
        value?: 'now' | 'live' | DateTime | null | undefined
        format?: string
        server?: boolean
        transform?: 'ucfirst' | null
    }

    let { value = undefined, format = 'yyyy-MM-dd', server = false, transform = null }: Props = $props()

    let now = $state(DateTime.now())
    if (value == 'live') {
        onMount(() => {
            const interval = setInterval(() => {
                now = DateTime.now()
            }, 1000)

            return () => {
                clearInterval(interval)
            }
        })
    }

    let effValue: DateTime | null = $derived.by(() => {
        return !value ? null : value == 'now' || value == 'live' ? (server ? alignToServer(now) : now) : toLuxon(value, { alignToServer: server })
    })

    let effFormat = $state(format)
    let formatFunc = $state('toFormat')
    $effect(() => {
        formatFunc = 'toFormat'
        if (format == 'table') {
            effFormat = effValue != null && effValue.hour < 10 ? 'yyyy-MM-dd \u00a0H:mm' : 'yyyy-MM-dd HH:mm'
        } else if (format == 'table-s') {
            effFormat = effValue != null && effValue.hour < 10 ? 'yyyy-MM-dd \u00a0H:mm:ss' : 'yyyy-MM-dd HH:mm:ss'
        } else if (format.match(/^[A-Z_]+$/) && DateTime[format]) {
            effFormat = DateTime[format]
            formatFunc = 'toLocaleString'
        } else {
            effFormat = format
        }
    })

    function doTransform(s: string, t: 'ucfirst' | null) {
        switch (t) {
            case 'ucfirst':
                return s[0].toLocaleUpperCase() + s.slice(1)
        }
        return s
    }
</script>

<style>
    .ht-datetime {
        font-feature-settings: 'tnum' 1;
    }
    .ht-datetime.sub-10 .dt-time {
        padding-left: 1.2ex;
    }
    .ht-date {
        font-feature-settings: 'tnum' 1;
    }
</style>

{#if ['h-table', 'h-table-s'].includes(effFormat)}
    {#if effValue == null}
        <span class="ht-datetime null">–</span>
    {:else}
        <span class="ht-datetime" class:sub-10={effValue.hour < 10} class:past-9={effValue.hour > 9} class:with-s={effFormat == 'h-table-s'} class:without-s={effFormat == 'h-table'}>
            <span class="dt-date">{effValue.toFormat('yyyy-MM-dd')}</span>
            <span class="dt-time">{effValue.toFormat((effValue.hour < 10 ? 'H:mm' : 'HH:mm') + (effFormat == 'h-table-s' ? ':ss' : ''))}</span>
        </span>
    {/if}
{:else if effFormat == 'h-table-date'}
    {#if effValue == null}
        <span class="ht-date null">–</span>
    {:else}
        <span class="ht-date">
            <span class="dt-date">{effValue.toFormat('yyyy-MM-dd')}</span>
        </span>
    {/if}
{:else if effValue == null}
    <span class="datetime null">–</span>
{:else}
    <span class="datetime">
        {doTransform(effValue.setLocale($luxonLocale)[formatFunc](effFormat), transform)}
    </span>
{/if}
